import { Button, Popover, PopoverContent, PopoverTrigger } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useMemo } from 'react';

import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { ProposalCommentButton } from '../ProposalCommentButton';

export const DetailDiffCommentButton = ({
  coordinates,
  parentComments,
  shouldShowPinForThreadIfExists,
  handleCommentButtonClick,
  testId,
}: {
  coordinates: string[];
  parentComments: GraphQLTypes.ProposalCommentsParentCommentFragment[];
  shouldShowPinForThreadIfExists: boolean;
  handleCommentButtonClick: (coordinate: string, commentId?: string) => void;
  testId?: string;
}) => {
  const replyCountsByCoordinate = useMemo(
    () =>
      coordinates.reduce(
        (acc, coordinate) => {
          const matchingParentComments = parentComments.filter(
            ({ schemaCoordinate }) => {
              return schemaCoordinate === coordinate;
            },
          );

          return {
            ...acc,
            ...Object.fromEntries(
              matchingParentComments.map(({ replyCount, id }) => [
                `${coordinate}-${id}`,
                replyCount ?? -1,
              ]),
            ),
          };
        },
        {} as Record<string, number>,
      ),
    [coordinates, parentComments],
  );

  const coordinatesWithThreads = Object.entries(replyCountsByCoordinate).filter(
    ([, replyCount]) => replyCount >= 0,
  );

  // In some cases (i.e. descriptions) we have multiple lines with the same coordinate
  // but we should only show the pin for the first line
  const shouldShowPinForThread =
    shouldShowPinForThreadIfExists &&
    isMinLengthArray(1, coordinatesWithThreads);

  const isMultiThreadLine = isMinLengthArray(2, coordinatesWithThreads);
  const isSingleThreadLine =
    !isMultiThreadLine && isMinLengthArray(1, coordinatesWithThreads);

  return (
    <Popover isLazy>
      <WrapMaybe
        // Popover is passed a child by WrapMaybe that overrides the one passed here
        // eslint-disable-next-line react/no-children-prop
        element={<PopoverTrigger children={undefined} />}
        on={isMultiThreadLine}
      >
        <ProposalCommentButton
          // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
          // See test here: https://app.datadoghq.com/synthetics/details/btp-g22-4vt
          data-testid={testId}
          className={classnames(
            'relative',
            !shouldShowPinForThread
              ? 'z-40 -mt-0.75 ml-2 opacity-0 focus-visible:opacity-100 group-hover:opacity-100'
              : 'z-30 -mt-px',
          )}
          {...(isMultiThreadLine
            ? {
                replyCount: coordinatesWithThreads.length,
                isMultiThreadLine: true,
              }
            : isSingleThreadLine && shouldShowPinForThread
              ? {
                  replyCount: coordinatesWithThreads[0][1] + 1,
                  onClick: () => {
                    const [coordinate, ...rest] =
                      coordinatesWithThreads[0][0].split('-');
                    const commentId = rest.join('-');
                    handleCommentButtonClick(coordinate, commentId);
                  },
                }
              : {
                  onClick: () =>
                    handleCommentButtonClick(coordinates[0] as string),
                })}
        />
      </WrapMaybe>
      {isMultiThreadLine && (
        <PopoverContent className="ml-10 max-w-60 p-2">
          {coordinatesWithThreads
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([coordinateWithId, replyCount], i) => {
              const [coordinate, ...rest] = coordinateWithId.split('-');
              const commentId = rest.join('-');
              return (
                <Button
                  variant="hidden"
                  size="sm"
                  key={coordinateWithId}
                  className="w-full justify-between"
                  onClick={() => {
                    handleCommentButtonClick(coordinate, commentId);
                  }}
                >
                  <div className="min-w-0 truncate">
                    <span className="truncate font-semibold">
                      Thread {i + 1}:
                    </span>{' '}
                    {coordinate}
                  </div>
                  <div className="ml-2">{replyCount + 1}</div>
                </Button>
              );
            })}
        </PopoverContent>
      )}
    </Popover>
  );
};
